import { Page } from '../../types'

const addPreventedFraud: Page[] = [
        {
        pageType: 'question',
        title: 'Prevented fraud & error',
        name: 'prevented-fraud-declare-page',
        description:
            'The Prevented categories are aligned to definitions referenced in the Fraud Measurement Standard:' +
            '<ul><li>Prevented Actuals (or Point of Interdiction): The Prevented Actuals typology refers to instances of payments prevented from being processed due to the detection of fraud, where the value of the payment prevented can be evidenced with certainty.</li><li>Future Loss Prevented: These are calculated savings that evidence that fraud/error would have continued for a period of time had it not been detected in the first place, and that subsequently would have resulted in a financial loss. These calculated savings should be arrived at through evidenced and agreed assumptions.</li><li>Upstream Prevention: Upstream savings are generated from the implementation of new processes, policies or controls that reduce the amount of fraud/error in a given payment stream or scheme. These calculated savings should refer to the reduction in the level of measured fraud/error in a payment stream or scheme from baseline, measured both post- and pre-implementation.</li></ul><br/>'+
            'The reporting in this section refers to instances where the prevention of fraud (and in some cases, error) can be evidenced with certainty or via an assured prevention methodology. Reported prevention is a result of active detection or evidenced via a measurement exercise. Values recorded here should be evidenced as either an evaluated or calculated saving, and should therefore reflect a realistic and legitimate saving/benefit to the exchequer. Only methodologies approved by the Prevention Panel can be used to calculate a saving.\n'+
            'The Prevention Panel is facilitated by the PSFA for use by all public bodies wishing to claim prevented savings based on an estimated value. A methodology should either be to calculate a Future Loss Prevented or Upstream Prevention estimate. The purpose is to ensure prevented estimates and the methodologies used to calculate savings are reasonable, robust and credible.\n'+
            'The Prevention Panel Methodology Bank provides a repository of all approved savings methodologies. In order for a new methodology to be approved by the Prevention Panel a Prevention Panel submission must be completed by the originating department/organisation. The submission must be sent to psfa.performance@cabinetoffice.gov.uk no later than three weeks prior to the next Prevention Panel and on the correct template. If you require further information about the process, the Methodology Bank or upcoming meeting dates please email psfa.performance@cabinetoffice.gov.uk.\n'+
            'Please Note: All entries in this section must be allocated a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail.',
        questions: [
            {
                questionType: 'radio',
                title: 'Do you have any instances of detected error to declare?',
                name: 'prevented-fraud-declare',
                options: ['Yes', 'No'],
                rules: {
                    required: true
                }
            }
        ]
    },
    {
        pageType: 'list',
        title: 'Add an instance of future prevented fraud',
        name: 'prevented-fraud-add-instance',
        description:
            'Prevented values should be evidenced with certainty. Prevented actuals should only refer to instances of fraud prevented, not error. Entries should also include selections from the Prevented Actuals typology dropdowns:'+
            '<ul><li>the business area where the fraud was prevented</li><li>the nature of deception - whether the prevented fraud involved a false representation, a failure to declare or disclose, or an abuse of position</li><li>the detailed nature of deception - a dependent dropdown selection on the specifics of the fraudulent activity</li><li>if applicable, the fraud enabler(s)</li><li>whether or not the fraud related to a COVID-19 scheme or activity</li></ul><br/>'+
            'Future loss prevented values should be determined through evidenced and agreed assumptions that a loss in the future was prevented as a result of detecting the fraud (e.g. payments would have continued). Savings should not be projected beyond a 12 month period. The savings methodology used should either be assured by the Prevention Panel, or refer to an approved methodology within the Prevention Panel Methodology Bank.',
        disabled: [
            {
                field: 'prevented-fraud-declare',
                condition: 'equals', // optional
                value: 'No'
            }
        ],
        questions: [
        {
                name: 'prevented-fraud-deception',
                questionType: 'radio',
                title: 'What was the general nature of deception?',
                detailsHeading: 'Help with general nature of deception',
                detailsContent: ' <b>Abuse of position:</b> use of a persons position or role in the goverment or public sector'+
                ' <br/><b>Failure to declare/disclose:</b> Intentionally omitting information you legally need to share' +                
                ' <br/><b>False Representation:</b> intentionally hiding or giving misleading information'+
                ' <br/><b>Unknown:</b> I dont know how the deception was committed.',
                options: [
                    {
                        option: 'Abuse of position',
                        questions: [
                            {
                                questionType: 'select',
                                title: 'Select',
                                name: 'prevented-fraud-abuse-of-positions',
                                options: ['Application for employment','Asset misappropriation','Contract rigging','Falsified application','Insider theft of assets','Mandate fraud',
                                    'Misuse of Government procurement card (GPC)','Payment fraud'],
                                rules: {
                                    required: true,
                                    isBeforeNow: 'enabled'
                                },
                                errorMessages: {
                                    required: 'You must select',
                                }
                            }
                        ]
                    },
                    {
                        option: 'Failure to declare/disclose',
                        questions: [
                            {
                                questionType: 'select',
                                title: 'Select',
                                name: 'prevented-fraud-declare-disclose',
                                options: ['Abuse of working hours','Application for employment','Asset misappropriation','Change of circumstances','Charge evasion','Misuse of funding',
                                    'Travel, Expenses and Subsistence'],
                                rules: {
                                    required: true,
                                    isBeforeNow: 'enabled'
                                },
                                errorMessages: {
                                    required: 'You must select',
                                }
                            }
                        ]
                    },
                    {
                        option: 'False Representation',
                        questions: [
                            {
                                questionType: 'select',
                                title: 'Select',
                                name: 'prvented-fraud-false-representation',
                                options: ['Abuse of working hours','Application for employment','Asset misappropriation','Charge evasion','Falsified application','Falsified invoice',
                                    'Mandate fraud','Misuse of funding','Product substitution/Misselling','Travel, Expenses and Subsistence'],
                                rules: {
                                    required: true,
                                    isBeforeNow: 'enabled'
                                },
                                errorMessages: {
                                    required: 'You must enter the date you took up residency in the UK',
                                    isBeforeNow: 'Date you took up residency must be in the past'
                                }
                            }
                        ]
                    },
                    'Unknown'
                ],
            },     
            {
                questionType: 'select',
                title: 'What was the specific nature of deception?',
                name: 'prevented-fraud-mandate',
                hintOption: 'Select an option',
                detailsHeading: 'Help with specific nature of deception',
                detailsContent: ' <b>Procurement:</b> Goods or services bought by public sector organisations (including suppliers)' +
                ' <br/><b>Personnel:</b> Employees (including contractors and agency workers).' +
                ' <br/><b>Subsidies:</b> Support or incentives given by the government.' +
                ' <br/><b>Grants:</b> Money given by a public sector organisation for a specific purpose.' +
                ' <br/><b>Loans & credit guarantees:</b> Money given by a public sector organisation for a specific purpose.' +
                ' <br/><b>Charges & other non-tax income:</b> Money owed to public sector organisations (for example, court fines or prescription fees)' +
                ' <br/><b>Licenses and permits:</b> Personal or business licenses (for example, a Blue Badge or Surveyor license)' +
                ' <br/><b>Tax & Duties:</b> Mandatory government charge for goods, income or services' +
                ' <br/><b>Benefits:</b> Money given by the government to support members of the public' +
                ' <br/><b>Special Payments:</b> Money given in unique or specific circumstances (for example, compensation)',
                options: ['Application for employment','Contract rigging','Asset misappropriation','Mandate fraud','Payment fraud','GPC fraud'],
                rules: {
                    required: true
                },
                errorMessages: { required: 'This field is mandatory' }
            },       
            {
                questionType: 'checkbox',
                title: 'What were the fraud enablers?',
                name: 'prevented-fraud-support',
                detailsHeading: 'Help with enablers',
                detailsContent: '<b>Social media:</b> using this to present disinformation (for example, falsifying personal details)'+
                                '<br><b>Phishing, Vishing and SMShing:</b> use cyber vulnerabilities using digital communication methods (for example, clicking a malicious link)'+
                                '<br><b>Bribery:</b> something given or promised to influence behaviour'+
                                '<br><b>Collusion:</b> deceptive agreement or cooperation (for example, due to lies or manipulation)'+
                                '<br><b>Coercion:</b> forced agreement or cooperation (for example, due to physical threats)'+
                                '<br><b>False identity:</b> using false documents as person or company (for example, a passport or birth certificate)'+
                                '<br><b>Conflict of interest:</b> not sharing relationships which resulted in personal gain'+
                                '<br><b>AI (artificial intelligence):</b> using artificial intelligence to create or present false information',
                options: ['Social media', 'Phishing, SMShing & Vishing', 'Bribery', 'Collusion','Coercion','False Identity','Conflict of Interest','AI (artificial intelligence'],
                rules: { required: true }
            },
            {
                questionType: 'select',
                title: 'Which spending area was the fraud prevented in?',
                name: 'prevented-fraud-area',
                detailsHeading: 'Help with spending areas',
                detailsContent: ' <b>Procurement:</b> Goods or services bought by public sector organisations (including suppliers)' +
                                ' <br/><b>Personnel:</b> Employees (including contractors and agency workers).' +
                                ' <br/><b>Subsidies:</b> Support or incentives given by the government.' +
                                ' <br/><b>Grants:</b> Money given by a public sector organisation for a specific purpose.' +
                                ' <br/><b>Loans & credit guarantees:</b> Money given by a public sector organisation for a specific purpose.' +
                                ' <br/><b>Charges & other non-tax income:</b> Money owed to public sector organisations (for example, court fines or prescription fees)' +
                                ' <br/><b>Licenses and permits:</b> Personal or business licenses (for example, a Blue Badge or Surveyor license)' +
                                ' <br/><b>Tax & Duties:</b> Mandatory government charge for goods, income or services' +
                                ' <br/><b>Benefits:</b> Money given by the government to support members of the public' +
                                ' <br/><b>Special Payments:</b> Money given in unique or specific circumstances (for example, compensation)',
                options: ['Procurement','Personnel','Subsidies','Grants','Loans & credit gurantees','Charges & other non-tax income','Licences and permits','Tax & Duties','Benefits','Special payments'],
                hintOption: 'Select an option',
                rules: {required: true},
                errorMessages: { required: 'This field is mandatory' }
            },    
            {
                questionType: 'instanceInput',
                title: 'How many instances were prevented?',
                name: 'prevented-fraud-impact',
                txt: `Total number of instances.`,
                rules: {
                    required: true
                }
            },
            {
                questionType: 'costInput',
                title: 'What was the value of the prevented fraud?',
                name: 'prevented-fraud-estimated-total',
                txt: `Total value`,
                rules: {
                    required: false
                }
            },            
            {
                questionType: 'textarea',
                title: 'Comments',
                name: 'prevented-fraud-comments',
                hint: `Please ensure you do not include any personal or sensitive information`,
                rules: {
                    required: false
                }
            },   
            {
                questionType: 'radio',
                title: 'Does this relate to BAU or C19 activity?',
                // hint: `You can tick both if applicable`,
                name: 'prevented-fraud-bau-c19',
                options: ['Business as usual (BAU)', 'Covid-19 (C19)'],
                rules: { required: true }
            }
        ]
    }
]
export default addPreventedFraud
